import type { GridApi } from 'ag-grid-community'
import { useAppSelector } from 'appState/store'
import { postbatchClaimsDownload } from 'features/autocheck/autoCheckApi'
import React, { useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { notificator } from 'utils/notificator/notificator'
import { DownloadModal } from './DownloadModal'
import { FilterFields } from './FilterFields'
import { FilterHeader } from './FilterHeader'
import { DEFAULT_FIELDS, type FieldIds } from './filterTypes'
import { useExternalFilter } from './useExternalFilter'

/**
 * Props for ExternalCustomFilter component
 */
type ExternalCustomFilterProps = {
  /** AG Grid API instance */
  api: GridApi | null | undefined
  /** Optional custom field ID mappings */
  fields?: Partial<FieldIds>
}

/**
 * ExternalCustomFilter Component
 *
 * Main component for managing external filters for the claims grid.
 * Provides a comprehensive filter UI with:
 * - Text input filters (Batch ID, Owner, Patient details, etc.)
 * - Date range filters (Last Modified, Date of Service)
 * - Status dropdown filter
 * - Filter persistence (saves to user preferences)
 * - Download functionality
 *
 * @example
 * ```tsx
 * <ExternalCustomFilter
 *   api={gridApi}
 *   fields={{ batchCode: 'customBatchField' }}
 * />
 * ```
 */
export const ExternalCustomFilter: React.FC<ExternalCustomFilterProps> = ({ api, fields: overrideFields }) => {
  // Merge custom field mappings with defaults
  const fields = useMemo(() => ({ ...DEFAULT_FIELDS, ...overrideFields }), [overrideFields])

  // Filter state and operations from custom hook
  const { form, lastModQuick, filterLength, updateFormField, setLastModQuick, applyFilters, clearAll } =
    useExternalFilter(api, fields)

  // Download modal state
  const [showDownloadModal, setShowDownloadModal] = useState(false)

  // Access claims data for download
  const obGridParams = useAppSelector((state: any) => state.claims)
  const totalItems = obGridParams?.claimsTable?.totalItems || 0

  // Ref for measuring filter height (used by parent)
  const filtersRef = useRef<HTMLDivElement>(null)

  /**
   * Handle download confirmation
   * Initiates the batch claims download process
   */
  const handleDownload = () => {
    if (!obGridParams?.apiQuery) {
      notificator.notify({
        type: 'error',
        heading: 'Download Error',
        message: 'Unable to download - data not available',
      })
      return
    }

    postbatchClaimsDownload(obGridParams.apiQuery).then((res) => {
      if (res) {
        setShowDownloadModal(false)
        notificator.notify({
          type: 'info',
          heading: 'Preparing file for download ⏳',
          message: 'Once your file is ready to download it will be available in the Notifications menu',
        })
      }
    })
  }

  return (
    <FilterContainer>
      {/* Filter Header with Actions */}
      <FilterHeader
        filterCount={filterLength}
        totalItems={totalItems}
        onSearch={applyFilters}
        onClear={clearAll}
        onDownload={() => setShowDownloadModal(true)}
      />

      {/* Filter Input Fields */}
      <FiltersWrap ref={filtersRef}>
        <FilterFields
          form={form}
          lastModQuick={lastModQuick}
          onUpdateField={updateFormField}
          onLastModQuickChange={setLastModQuick}
        />
      </FiltersWrap>

      {/* Download Confirmation Modal */}
      <DownloadModal
        show={showDownloadModal}
        totalItems={totalItems}
        onClose={() => setShowDownloadModal(false)}
        onConfirm={handleDownload}
      />
    </FilterContainer>
  )
}

/* ---------- Styled Components ---------- */

// Main Container
const FilterContainer = styled.div`
  border-radius: ${({ theme }) => theme.spacing.xs};
  box-shadow: 0 8px 8px 0 rgba(0, 0, 0, 0.06);
  border: solid 1px ${({ theme }) => theme.colors.grey.lighter};
  background-color: ${({ theme }) => theme.colors.grey.lightest};
  padding: ${({ theme }) => theme.spacing.xs};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
  overflow: visible;
  width: 100%;
`

const FiltersWrap = styled.div`
  overflow: visible;
`
